import { z } from 'zod';
import {
  storedFileSchema,
  writeOffStatusSchema,
  type CreateWriteOffInput,
  type StoredFileDto,
  type WriteOffActDto,
  type WriteOffListRow,
} from '@eombor/contracts';
import type { ApiClient } from '../../../shared/api/client.js';
import { apiClient } from '../../auth/model/auth.store.js';

/**
 * RASXOD — API yuzasi.
 *
 * Ro'yxat IKKI manzilda va bu backenddagi bilan ayni sabab: xodimning
 * O'Z aktlari va moddiy buxgalterning NAVBATI boshqa-boshqa savolga
 * javob beradi. Bitta manzilga bayroq qo'yilsa, bayroqni unutgan
 * chaqiruv buyurtmachiga hamkasblarining aktlarini ochib qo'yardi.
 */

const reviewSchema = z.object({
  id: z.string(),
  decision: z.enum(['APPROVE', 'REJECT']),
  reviewerId: z.string(),
  reviewerName: z.string().nullable(),
  reason: z.string().nullable(),
  decidedAt: z.string(),
});

const commonSchema = z.object({
  id: z.string(),
  docNo: z.string(),
  status: writeOffStatusSchema,
  subdivisionId: z.string(),
  subdivisionName: z.string(),
  batchId: z.string(),
  batchNo: z.string(),
  nomenclatureId: z.string(),
  nomenclatureName: z.string(),
  unit: z.string(),
  qty: z.number(),
  stockQtyBefore: z.number().nullable(),
  requisitionId: z.string().nullable(),
  requisitionDocNo: z.string().nullable(),
  actFileId: z.string(),
  locationLat: z.number().nullable(),
  locationLng: z.number().nullable(),
  locationAccuracy: z.number().nullable(),
  reason: z.string(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  submittedAt: z.string(),
});

/** Rasmning o'rnini bosadigan dalil — ilovada olinganlar. */
const usageSchema = z.object({
  id: z.string(),
  usedAt: z.string(),
  qty: z.number(),
  comment: z.string(),
  workerName: z.string(),
});

const actSchema = commonSchema.extend({
  photoFileIds: z.array(z.string()),
  usages: z.array(usageSchema),
  reviews: z.array(reviewSchema),
});

const listRowSchema = commonSchema.extend({
  photoCount: z.number(),
  lastRejectReason: z.string().nullable(),
});

export const writeOffPageSchema = z.object({
  items: z.array(listRowSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export type WriteOffPage = z.infer<typeof writeOffPageSchema>;

export interface WriteOffQuery {
  status?: string;
  page?: number;
  limit?: number;
}

export interface WriteOffApi {
  /** Xodimning O'Z aktlari. */
  listMine: (query?: WriteOffQuery) => Promise<WriteOffPage>;
  /** Moddiy buxgalter navbati — doiralangan. */
  listQueue: (query?: WriteOffQuery) => Promise<WriteOffPage>;
  get: (id: string) => Promise<WriteOffActDto>;
  create: (input: CreateWriteOffInput) => Promise<WriteOffActDto>;
  approve: (id: string) => Promise<WriteOffActDto>;
  reject: (id: string, reason: string) => Promise<WriteOffActDto>;
  resubmit: (id: string) => Promise<WriteOffActDto>;
  cancel: (id: string) => Promise<WriteOffActDto>;
  /** Akt qog'ozi yoki rasm — kontekst bo'yicha ayriladi. */
  upload: (file: File, context: 'WRITE_OFF_ACT' | 'WRITE_OFF_PHOTO') => Promise<StoredFileDto>;
}

const queryString = (query: WriteOffQuery): string => {
  const params = new URLSearchParams();
  if (query.status !== undefined) params.set('status', query.status);
  params.set('page', String(query.page ?? 1));
  params.set('limit', String(query.limit ?? 20));
  return `?${params.toString()}`;
};

const action = (client: ApiClient, id: string, name: string, body?: unknown) =>
  client.apiFetch(`/write-offs/${id}/${name}`, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
    schema: actSchema,
  });

export const createWriteOffApi = (client: ApiClient): WriteOffApi => ({
  listMine: (query = {}) =>
    client.apiFetch(`/write-offs/mine${queryString(query)}`, { schema: writeOffPageSchema }),

  listQueue: (query = {}) =>
    client.apiFetch(`/write-offs${queryString(query)}`, { schema: writeOffPageSchema }),

  get: (id) => client.apiFetch(`/write-offs/${id}`, { schema: actSchema }),

  create: (input) =>
    client.apiFetch('/write-offs', {
      method: 'POST',
      body: JSON.stringify(input),
      schema: actSchema,
    }),

  approve: (id) => action(client, id, 'approve'),
  reject: (id, reason) => action(client, id, 'reject', { reason }),
  resubmit: (id) => action(client, id, 'resubmit'),
  cancel: (id) => action(client, id, 'cancel'),

  upload: (file, context) => {
    const form = new FormData();
    form.set('context', context);
    form.set('file', file);
    // `Content-Type` ATAYLAB qo'yilmaydi — brauzer uni `boundary` bilan yozadi.
    return client.apiFetch('/files', { method: 'POST', body: form, schema: storedFileSchema });
  },
});

export const writeOffApi = createWriteOffApi(apiClient);
export type { WriteOffActDto, WriteOffListRow };
